// Reunderwriting Module (Tier 3) PR 4 — escalations list table.

import { Badge, Button } from "@/design-system/components/ui";
import type {
  PagedReunderwritingEscalations,
  ReunderwritingEscalationStatus,
} from "@/types/reunderwriting";
import { useDateFormatter } from "@/utils/datetime";

function statusBadge(s: ReunderwritingEscalationStatus) {
  if (s === "Pending") return <Badge status="gold">Pending</Badge>;
  if (s === "Approved") return <Badge status="finalized">Approved</Badge>;
  return <Badge status="error">Denied</Badge>;
}

function truncate(s: string | null, n = 80) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

interface Props {
  page: PagedReunderwritingEscalations;
  sort: string;
  onSortChange: (s: string) => void;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
  onOpen: (id: string) => void;
}

export function ReunderwritingEscalationList({
  page,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onOpen,
}: Props) {
  const { dateTime } = useDateFormatter();
  const totalPages = Math.max(1, Math.ceil(page.totalCount / page.pageSize));

  return (
    <div className="flex flex-col gap-space-3">
      <table className="w-full text-small" aria-label="Reunderwriting escalations">
        <thead className="text-caption text-gray-mid text-left">
          <tr>
            <th className="py-space-1">Status</th>
            <th>Item</th>
            <th>Case</th>
            <th>Analyst</th>
            <th>Reason</th>
            <th>
              <button
                type="button"
                className="underline"
                onClick={() =>
                  onSortChange(
                    sort === "escalatedAt:desc" ? "escalatedAt:asc" : "escalatedAt:desc",
                  )
                }
              >
                Escalated
              </button>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {page.items.map((row) => (
            <tr key={row.id} className="border-t border-border-light">
              <td className="py-space-2">{statusBadge(row.status)}</td>
              <td>
                <div className="font-medium">{row.checklistItemCode ?? "—"}</div>
                <div className="text-caption text-gray-mid">
                  {truncate(row.checklistItemTitle, 60)}
                </div>
              </td>
              <td className="font-mono">{row.caseNumber ?? "—"}</td>
              <td className="text-caption">{row.escalatedByUserId}</td>
              <td title={row.escalationReason}>
                <em>{truncate(row.escalationReason, 80)}</em>
              </td>
              <td className="text-caption">
                {dateTime(row.escalatedAt)}
              </td>
              <td>
                <Button variant="tertiary" size="sm" onClick={() => onOpen(row.id)}>
                  View →
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between text-caption">
        <div>
          Page {page.page} of {totalPages} ({page.totalCount} total)
        </div>
        <div className="flex items-center gap-space-2">
          <Button
            variant="tertiary"
            size="sm"
            disabled={page.page <= 1}
            onClick={() => onPageChange(page.page - 1)}
          >
            ‹ Prev
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            disabled={page.page >= totalPages}
            onClick={() => onPageChange(page.page + 1)}
          >
            Next ›
          </Button>
          <select
            value={page.pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/page
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
